import { useState } from 'react';
import { INTEREST_PAYMENTS_ADD } from 'adminConstants/routes';
import { numberToCurrencyString } from 'further-ui/utils';
import { Button, CircularProgress } from '@mui/material';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import PageContainer from 'components/PageContainer';
import DownloadSheet from './CreateSheetForm';
import FullPageSuccess from 'components/FullPageSuccess';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import RecordView from 'components/RecordView';
import useFileUpload from 'hooks/ui/useFileUpload';
import api, { ApiResponses } from 'lib/trpcClient';
import { UploadType } from 'further-types/files';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import IssueFeedbackTable from './IssueFeedbackTable';
import { Row } from 'components/Layout';

const useStyles = makeStyles()((theme) => ({
  downloadForm: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    '& > div': {
      padding: '12px 0',
    },
  },
  note: {
    fontSize: 16,
    color: '#656565',
    marginBottom: theme.spacing(6),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1rem',
  },
  loadingNote: {
    fontStyle: 'italic',
    marginLeft: '1rem',
    color: '#656565',
  },
  textLink: {
    textDecoration: 'underline',
    color: theme.palette.text.rootColor,
  },
  uploadButton: {
    width: '135px',
    textTransform: 'none',
  },
}));

const STEPS = {
  DOWNLOAD_SHEET: 0,
  UPLOAD_SHEET: 1,
  UPLOAD_SUCCESS: 2,
};

const InterestPaymentCreate = () => {
  const { classes } = useStyles();
  const { error } = useNotification();

  const [state, setState] = useState<
    ApiResponses['interestPayment']['processUpload'] & {
      activeStep: number;
      filePath: string;
    }
  >({
    activeStep: STEPS.DOWNLOAD_SHEET,
    totalRowsIngested: 0,
    totalAmountIngested: 0,
    errors: [],
    warnings: [],
    filePath: '',
  });
  const processUpload = api.interestPayment.processUpload.useMutation(
    useHandleTrpcMutation()({}),
  );
  const isUploading = processUpload.isPending;

  const { uploadFile } = useFileUpload();

  const handleUploadButtonClick = (event) => {
    const { target = {} } = event || {};
    target.value = '';
  };

  const handleUpload = async ({
    filePath,
    ignoreWarnings,
  }: {
    filePath: string;
    ignoreWarnings: boolean;
  }) => {
    const processUploadResult = await processUpload.mutateAsync({
      filePath,
      ignoreWarnings,
    });
    if (
      processUploadResult.errors.length ||
      (processUploadResult.warnings.length && !ignoreWarnings)
    ) {
      setState({
        ...state,
        filePath,
        errors: processUploadResult.errors,
        warnings: processUploadResult.warnings,
      });
    } else {
      setState({
        ...state,
        filePath: '',
        activeStep: STEPS.UPLOAD_SUCCESS,
        totalRowsIngested: processUploadResult.totalRowsIngested,
        totalAmountIngested: processUploadResult.totalAmountIngested,
        errors: [],
        warnings: [],
      });
    }
  };

  const handleUploadButtonChange = async (event) => {
    if (event.target.files?.length) {
      const { data, error: uploadError } = await uploadFile(
        event.target.files[0],
        UploadType.InterestPayments,
      );
      if (data) {
        handleUpload({ filePath: data.filePath, ignoreWarnings: false });
      } else {
        error(uploadError.message);
      }
    }
  };

  const heading = 'Interest: Upload interest payments';

  if (state.activeStep === STEPS.UPLOAD_SUCCESS) {
    return (
      <PageContainer heading={heading}>
        <FullPageSuccess
          title="Your interest payments have been successfully added"
          additionalText={`${numberToCurrencyString(
            state.totalAmountIngested,
          )} has been sent to ${
            state.totalRowsIngested
          } investors, and will now be visible within their investor cash balances.`}
          proceedText="Upload more interest payments"
          proceedLink={INTEREST_PAYMENTS_ADD}
          proceedCallback={() => {
            setState({ ...state, activeStep: STEPS.DOWNLOAD_SHEET });
          }}
        />
      </PageContainer>
    );
  }

  const hasWarningsOnly =
    state.warnings.length > 0 && state.errors.length === 0;

  return (
    <PageContainer heading={heading}>
      <PageContent>
        <RecordView>
          {state.activeStep in [STEPS.DOWNLOAD_SHEET, STEPS.UPLOAD_SHEET] && (
            <PageSection title="1. Create an upload sheet">
              <DownloadSheet
                callback={() => {
                  setState({ ...state, activeStep: STEPS.UPLOAD_SHEET });
                }}
                //@ts-expect-error
                skipCallback={
                  state.activeStep === STEPS.DOWNLOAD_SHEET
                    ? () => {
                        setState({
                          ...state,
                          activeStep: STEPS.UPLOAD_SHEET,
                        });
                      }
                    : undefined
                }
              />
            </PageSection>
          )}
          {state.activeStep === STEPS.UPLOAD_SHEET && (
            <PageSection title="2. Upload your interest sheet" showDivider>
              <div className={classes.note}>
                Please check your download folder for your interest sheet.
                Please fill in the interest amounts and upload the sheet here.
                Form didn't download?{' '}
                <a
                  className={classes.textLink}
                  href={INTEREST_PAYMENTS_ADD}
                  title="Try again"
                  onClick={(e) => {
                    e.preventDefault();
                    setState({ ...state, activeStep: STEPS.DOWNLOAD_SHEET });
                  }}
                >
                  Click here to try again.
                </a>
              </div>
              <Grid size={{ md: 12 }}>
                <Row spacing="xs">
                  <Button
                    variant="outlined"
                    component="label"
                    color="primary"
                    disabled={isUploading}
                    className={classes.uploadButton}
                  >
                    Upload
                    <input
                      disabled={
                        state.activeStep === STEPS.UPLOAD_SUCCESS || isUploading
                      }
                      name="uploadSpreadsheet"
                      type="file"
                      hidden
                      onChange={handleUploadButtonChange}
                      onClick={handleUploadButtonClick}
                    />
                  </Button>
                  {hasWarningsOnly ? (
                    <Button
                      variant="contained"
                      component="label"
                      color="primary"
                      disabled={isUploading}
                      onClick={() =>
                        handleUpload({
                          filePath: state.filePath,
                          ignoreWarnings: true,
                        })
                      }
                    >
                      Proceed anyway
                    </Button>
                  ) : null}
                </Row>
                {isUploading && (
                  <div className={classes.loading}>
                    <CircularProgress size={18} />
                    <span className={classes.loadingNote}>
                      This may take a minute or two for large files.
                    </span>
                  </div>
                )}
              </Grid>
              {state.errors && state.errors.length > 0 && (
                <IssueFeedbackTable
                  issues={state.errors}
                  tableContentsType="errors"
                />
              )}
              {state.warnings && state.warnings.length > 0 && (
                <IssueFeedbackTable
                  issues={state.warnings}
                  tableContentsType="warnings"
                />
              )}
            </PageSection>
          )}
        </RecordView>
      </PageContent>
    </PageContainer>
  );
};

export default InterestPaymentCreate;
